import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  addTracksToMixtape,
  createMixtape,
  fetchMyMixtapes,
  MixtapeSummary,
} from "@/services/mixtapeService";

interface Props {
  open: boolean;
  trackId?: string;
  onClose: () => void;
}

const AddToMixtapeDialog: React.FC<Props> = ({ open, trackId, onClose }) => {
  const [mixtapes, setMixtapes] = useState<MixtapeSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [creatingNew, setCreatingNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchMyMixtapes()
      .then((res) => {
        const mixtapeList = res?.mixtapes ?? [];
        setMixtapes(mixtapeList);
        if (mixtapeList.length) {
          const defaultId = mixtapeList[0]?.id;
          if (defaultId) {
            setSelectedId((prev) => prev || defaultId);
          }
        }
      })
      .catch((err) => {
        console.error("Failed to load mixtapes", err);
        toast.error(err?.message || "Failed to load mixtapes");
      })
      .finally(() => setLoading(false));
  }, [open]);

  const resetForm = () => {
    setCreatingNew(false);
    setNewTitle("");
    setNewDescription("");
    setIsPublic(true);
  };

  const handleSubmit = async () => {
    if (!trackId) {
      toast.error("Track information missing");
      return;
    }

    try {
      setSubmitting(true);
      let targetId = selectedId;

      if (creatingNew) {
        if (!newTitle.trim()) {
          toast.error("Mixtape title is required");
          return;
        }
        const { mixtape } = await createMixtape({
          title: newTitle.trim(),
          description: newDescription.trim() || undefined,
          is_public: isPublic,
        });
        setMixtapes((prev) => [mixtape, ...prev]);
        targetId = mixtape.id;
        setSelectedId(mixtape.id);
      }

      if (!targetId) {
        toast.error("Select a mixtape");
        return;
      }

      await addTracksToMixtape(targetId, [trackId]);
      toast.success("Track added to mixtape");
      onClose();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error((err as Error)?.message || "Failed to add track");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
    resetForm();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add to Mixtape</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {!creatingNew && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Select one of your mixtapes
                </Typography>
                {mixtapes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    You don't have any mixtapes yet. Create one below.
                  </Typography>
                ) : (
                  <List dense>
                    {mixtapes.map((mix) => (
                      <ListItemButton
                        key={mix.id}
                        selected={selectedId === mix.id}
                        onClick={() => setSelectedId(mix.id)}
                      >
                        <ListItemText
                          primary={mix.title}
                          secondary={mix.is_public ? "Public" : "Private"}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </>
            )}

            <Box sx={{ my: 2 }}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  or
                </Typography>
              </Divider>
            </Box>

            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={creatingNew}
                    onChange={(e) => setCreatingNew(e.target.checked)}
                  />
                }
                label="Create new mixtape"
              />
            </Box>

            {creatingNew && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Mixtape name"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                    />
                  }
                  label={isPublic ? "Public" : "Private"}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || (!creatingNew && !selectedId)}
        >
          {submitting ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToMixtapeDialog;
